/**
 * EquipmentMenuUI.js - 装备菜单 UI（槽位导向布局）
 * 左侧：人物剪影 + 装备槽；右侧：当前槽位详情；底部：背包装备列表
 */
import EquipmentSystem from './EquipmentSystem.js';
import UIStyleHelper from './UIStyleHelper.js';
import { resolveEquipmentIconKey } from './ItemIconHelper.js';

const POSITION_SCALE = 2.5;
const SLOT_SIZE = 48;

export default class EquipmentMenuUI {
    constructor(scene) {
        this.scene = scene;
        this.elements = {
            menuList: null,
            infoPanel: null,
            bottomPanel: null,
            slotEntries: {},
            rightTexts: [],
            bottomTexts: [],
            hintText: null
        };
        this.layout = null;
        this.onSlotSelect = null;
        this.onInventorySelect = null;
        this.onInventoryConfirm = null;
    }

    setContainers(menuList, infoPanel) {
        this.elements.menuList = menuList;
        this.elements.infoPanel = infoPanel;
    }

    /**
     * 根据人体槽位坐标做方向导航
     */
    static getAdjacentSlotIndex(slotIndex, direction) {
        const order = EquipmentSystem.SLOT_ORDER;
        const currentSlot = order[slotIndex];
        if (!currentSlot) return slotIndex;

        const currentPos = EquipmentSystem.SLOT_POSITIONS[currentSlot];
        let bestIndex = slotIndex;
        let bestScore = Infinity;

        order.forEach((slot, index) => {
            if (index === slotIndex) return;
            const pos = EquipmentSystem.SLOT_POSITIONS[slot];
            const dx = pos.x - currentPos.x;
            const dy = pos.y - currentPos.y;

            let aligned = false;
            switch (direction) {
                case 'up': aligned = dy < -6; break;
                case 'down': aligned = dy > 6; break;
                case 'left': aligned = dx < -6; break;
                case 'right': aligned = dx > 6; break;
                default: return;
            }
            if (!aligned) return;

            const perp = (direction === 'up' || direction === 'down') ? Math.abs(dx) : Math.abs(dy);
            const score = dx * dx + dy * dy + perp * 40;
            if (score < bestScore) {
                bestScore = score;
                bestIndex = index;
            }
        });

        return bestIndex;
    }

    /**
     * 显示装备菜单
     */
    showEquipmentMenu(playerData, slotIndex, inventoryIndex, onSlotSelect, onInventorySelect, onInventoryConfirm) {
        this.onSlotSelect = onSlotSelect;
        this.onInventorySelect = onInventorySelect;
        this.onInventoryConfirm = onInventoryConfirm;
        this.clear();

        const cam = this.scene.cameras.main;
        this.layout = {
            leftX: cam.width * 0.28,
            leftY: cam.centerY - 8,
            rightX: cam.width * 0.72,
            rightY: cam.centerY - 50,
            bottomY: cam.height - 78,
            bottomWidth: cam.width * 0.88
        };

        this.elements.menuList.setPosition(0, 0);
        this.elements.infoPanel.setPosition(0, 0);

        this._createBottomPanel();
        this._drawLeftPanel(playerData, slotIndex);
        this._drawRightPanel(playerData, slotIndex);
        this._drawBottomPanel(playerData, slotIndex, inventoryIndex);
        this._drawHint(cam);
    }

    /**
     * 仅更新选中状态（不重建整个人物面板）
     */
    updateSelection(playerData, slotIndex, inventoryIndex) {
        this._updateSlotHighlights(slotIndex, playerData);
        this._redrawRightPanel(playerData, slotIndex);
        this._redrawBottomPanel(playerData, slotIndex, inventoryIndex);
    }

    _createBottomPanel() {
        if (this.elements.bottomPanel) {
            this.elements.bottomPanel.destroy();
        }
        const cam = this.scene.cameras.main;
        this.elements.bottomPanel = this.scene.add.container(cam.width / 2, this.layout.bottomY);
        this.elements.bottomPanel.setDepth(10001);
        this.elements.bottomPanel.setScrollFactor(0);
    }

    _drawLeftPanel(playerData, selectedSlotIndex) {
        const { leftX, leftY } = this.layout;
        this.elements.slotEntries = {};

        const title = this._addToMenuList(leftX, leftY - 200, '角色装备', {
            fontSize: '16px',
            fill: '#ffffff',
            fontFamily: 'monospace'
        });
        title.setOrigin(0.5);

        this._drawSilhouette(leftX, leftY, playerData);

        const equippedMap = this._getEquippedMap(playerData);

        EquipmentSystem.SLOT_ORDER.forEach((slot, index) => {
            const pos = EquipmentSystem.SLOT_POSITIONS[slot];
            const slotX = leftX + pos.x * POSITION_SCALE;
            const slotY = leftY + pos.y * POSITION_SCALE;
            const equippedItem = equippedMap[slot];
            const isSelected = index === selectedSlotIndex;

            const border = this.scene.add.graphics();
            this._drawSlotBorder(border, slotX, slotY, isSelected, Boolean(equippedItem));
            this.elements.menuList.add(border);

            let content = null;
            if (equippedItem) {
                const iconKey = resolveEquipmentIconKey(this.scene, equippedItem);
                if (iconKey) {
                    content = this.scene.add.image(slotX, slotY, iconKey);
                    content.setDisplaySize(SLOT_SIZE - 8, SLOT_SIZE - 8);
                } else {
                    content = this._addToMenuList(slotX, slotY, equippedItem.name, {
                        fontSize: '10px',
                        fill: '#ffffff',
                        fontFamily: 'monospace',
                        wordWrap: { width: SLOT_SIZE - 6 },
                        align: 'center'
                    }).setOrigin(0.5);
                }
            } else {
                const shortName = EquipmentSystem.getSlotChineseName(slot).slice(0, 2);
                content = this._addToMenuList(slotX, slotY, shortName, {
                    fontSize: '11px',
                    fill: '#666666',
                    fontFamily: 'monospace'
                }).setOrigin(0.5);
            }
            if (content) this.elements.menuList.add(content);

            const hit = this.scene.add.rectangle(slotX, slotY, SLOT_SIZE + 8, SLOT_SIZE + 8, 0x000000, 0);
            hit.setInteractive({ useHandCursor: true });
            hit.on('pointerdown', () => {
                if (this.onSlotSelect) this.onSlotSelect(index);
            });
            this.elements.menuList.add(hit);

            this.elements.slotEntries[slot] = { border, content, hit, slotX, slotY, index };
        });
    }

    _drawSilhouette(centerX, centerY, playerData) {
        let silhouetteKey = 'equipment_silhouette_p1';
        if (playerData?.element === 'player2') silhouetteKey = 'equipment_silhouette_p2';
        else if (playerData?.element === 'player3') silhouetteKey = 'equipment_silhouette_p3';

        if (this.scene.textures.exists(silhouetteKey)) {
            const img = this.scene.add.image(centerX, centerY, silhouetteKey);
            const displayHeight = 320;
            img.setDisplaySize(displayHeight * (848 / 2048), displayHeight);
            img.setAlpha(0.75);
            this.elements.menuList.add(img);
        } else {
            const g = this.scene.add.graphics();
            g.lineStyle(2, 0x555555, 0.6);
            g.strokeCircle(centerX, centerY - 120, 18);
            g.strokeRect(centerX - 24, centerY - 90, 48, 70);
            this.elements.menuList.add(g);
        }
    }

    _drawSlotBorder(graphics, x, y, isSelected, isEquipped) {
        graphics.clear();
        const half = SLOT_SIZE / 2;
        if (isSelected) {
            graphics.lineStyle(3, 0xffff00, 1);
            graphics.fillStyle(0xffff00, 0.15);
        } else if (isEquipped) {
            graphics.lineStyle(2, 0x4cc9f0, 0.9);
            graphics.fillStyle(0x4cc9f0, 0.12);
        } else {
            graphics.lineStyle(2, 0x666666, 0.7);
            graphics.fillStyle(0x222222, 0.5);
        }
        graphics.fillRect(x - half, y - half, SLOT_SIZE, SLOT_SIZE);
        graphics.strokeRect(x - half, y - half, SLOT_SIZE, SLOT_SIZE);
    }

    _updateSlotHighlights(selectedSlotIndex, playerData) {
        const equippedMap = this._getEquippedMap(playerData);
        Object.entries(this.elements.slotEntries).forEach(([slot, entry]) => {
            const isSelected = entry.index === selectedSlotIndex;
            this._drawSlotBorder(entry.border, entry.slotX, entry.slotY, isSelected, Boolean(equippedMap[slot]));
        });
    }

    _drawRightPanel(playerData, slotIndex) {
        this._redrawRightPanel(playerData, slotIndex);
    }

    _redrawRightPanel(playerData, slotIndex) {
        this._clearTexts(this.elements.rightTexts);
        this.elements.infoPanel.removeAll(true);
        this.elements.rightTexts = [];

        const slot = EquipmentSystem.SLOT_ORDER[slotIndex];
        const slotName = EquipmentSystem.getSlotChineseName(slot);
        const equipped = EquipmentSystem.getEquippedItem(playerData, slot);
        const { rightX, rightY } = this.layout;
        let y = rightY;

        const title = this._addToInfoPanel(rightX, y, `【${slotName}】`, {
            fontSize: '18px',
            fill: '#4cc9f0',
            fontFamily: 'monospace',
            fontStyle: 'bold'
        }).setOrigin(0.5, 0);
        this.elements.rightTexts.push(title);
        y += 32;

        if (!equipped) {
            this.elements.rightTexts.push(
                this._addToInfoPanel(rightX, y, '当前：未装备', {
                    fontSize: '15px',
                    fill: '#888888',
                    fontFamily: 'monospace'
                }).setOrigin(0.5, 0)
            );
            y += 28;
            this.elements.rightTexts.push(
                this._addToInfoPanel(rightX, y, '在底部选择背包装备后按空格穿上', {
                    fontSize: '12px',
                    fill: '#aaaaaa',
                    fontFamily: 'monospace',
                    wordWrap: { width: 260 },
                    align: 'center'
                }).setOrigin(0.5, 0)
            );
            return;
        }

        const lines = [
            `名称：${equipped.name}`,
            `类型：${slotName}`
        ];
        if (equipped.damage) lines.push(`攻击：+${equipped.damage}`);
        if (equipped.defense) lines.push(`防御：+${equipped.defense}`);
        if (equipped.range) lines.push(`射程：${equipped.range}`);
        if (equipped.description) lines.push(`描述：${equipped.description}`);

        lines.forEach((line) => {
            const t = this._addToInfoPanel(rightX, y, line, {
                fontSize: '14px',
                fill: '#ffffff',
                fontFamily: 'monospace',
                wordWrap: { width: 280 },
                align: 'left'
            }).setOrigin(0.5, 0);
            this.elements.rightTexts.push(t);
            y += 24;
        });

        this.elements.rightTexts.push(
            this._addToInfoPanel(rightX, y + 8, '按空格卸下（旧装备回到背包）', {
                fontSize: '12px',
                fill: '#aaaaaa',
                fontFamily: 'monospace'
            }).setOrigin(0.5, 0)
        );
    }

    _drawBottomPanel(playerData, slotIndex, inventoryIndex) {
        this._redrawBottomPanel(playerData, slotIndex, inventoryIndex);
    }

    _redrawBottomPanel(playerData, slotIndex, inventoryIndex) {
        this._clearTexts(this.elements.bottomTexts);
        if (this.elements.bottomPanel) {
            this.elements.bottomPanel.removeAll(true);
        }
        this.elements.bottomTexts = [];

        const slot = EquipmentSystem.SLOT_ORDER[slotIndex];
        const slotName = EquipmentSystem.getSlotChineseName(slot);
        const items = this._getEquipableItems(slot, playerData);
        const { bottomWidth } = this.layout;
        let y = -52;

        const header = this.scene.add.text(0, y, `可装备 · ${slotName}`, {
            fontSize: '14px',
            fill: '#4cc9f0',
            fontFamily: 'monospace'
        }).setOrigin(0.5, 0);
        this.elements.bottomPanel.add(header);
        this.elements.bottomTexts.push(header);
        y += 26;

        if (items.length === 0) {
            const empty = this.scene.add.text(0, y, '背包中无可装备物品', {
                fontSize: '13px',
                fill: '#666666',
                fontFamily: 'monospace'
            }).setOrigin(0.5, 0);
            this.elements.bottomPanel.add(empty);
            this.elements.bottomTexts.push(empty);
            return;
        }

        const safeIndex = Phaser.Math.Clamp(inventoryIndex, 0, items.length - 1);
        const lineHeight = 22;
        const maxVisible = 5;
        let start = Math.max(0, safeIndex - 2);
        if (start + maxVisible > items.length) {
            start = Math.max(0, items.length - maxVisible);
        }
        const visible = items.slice(start, start + maxVisible);

        visible.forEach((item, i) => {
            const actualIndex = start + i;
            const isSelected = actualIndex === safeIndex;
            let label = item.name;
            if (item.damage) label += `  攻+${item.damage}`;
            else if (item.defense) label += `  防+${item.defense}`;

            const text = this.scene.add.text(0, y, (isSelected ? '▶ ' : '  ') + label, {
                fontSize: '13px',
                fill: UIStyleHelper.getMenuItemFill(isSelected),
                fontFamily: 'monospace'
            }).setOrigin(0.5, 0);
            text.setInteractive({ useHandCursor: true });
            text.on('pointerover', () => {
                if (this.onInventorySelect) this.onInventorySelect(actualIndex);
            });
            text.on('pointerdown', () => {
                if (this.onInventorySelect) this.onInventorySelect(actualIndex);
                if (this.onInventoryConfirm) this.onInventoryConfirm(actualIndex);
            });
            this.elements.bottomPanel.add(text);
            this.elements.bottomTexts.push(text);
            y += lineHeight;
        });

        if (items.length > maxVisible) {
            const more = this.scene.add.text(0, y, `… 共 ${items.length} 件，Q/E 切换`, {
                fontSize: '11px',
                fill: '#666666',
                fontFamily: 'monospace'
            }).setOrigin(0.5, 0);
            this.elements.bottomPanel.add(more);
            this.elements.bottomTexts.push(more);
        }
    }

    _drawHint(cam) {
        if (this.elements.hintText) {
            this.elements.hintText.destroy();
        }
        this.elements.hintText = this.scene.add.text(
            cam.width / 2,
            cam.height - 18,
            'W/S/A/D 或方向键选部位 · Q/E 选背包装备 · 空格 装备/卸下 · Esc 返回主菜单',
            {
                fontSize: '11px',
                fill: '#666666',
                fontFamily: 'monospace',
                align: 'center'
            }
        ).setOrigin(0.5);
        this.elements.hintText.setDepth(10002);
        this.elements.hintText.setScrollFactor(0);
    }

    _getEquippedMap(playerData) {
        const map = {};
        (playerData.equipment || []).forEach((item) => {
            if (item?.slot) {
                map[EquipmentSystem.normalizeSlot(item.slot)] = item;
            }
        });
        return map;
    }

    _getEquipableItems(slot, playerData) {
        return (playerData.inventory || []).filter((item) => {
            if (!item) return false;
            let itemSlot = item.slot || EquipmentSystem.getSlotFromType(item.type);
            if (itemSlot) itemSlot = EquipmentSystem.normalizeSlot(itemSlot);
            return itemSlot === slot && EquipmentSystem.canEquip(item, playerData);
        });
    }

    _addToMenuList(x, y, text, style) {
        const t = this.scene.add.text(x, y, text, style);
        this.elements.menuList.add(t);
        return t;
    }

    _addToInfoPanel(x, y, text, style) {
        const t = this.scene.add.text(x, y, text, style);
        this.elements.infoPanel.add(t);
        return t;
    }

    _clearTexts(texts) {
        texts.forEach((t) => {
            if (t?.destroy) t.destroy();
        });
    }

    clear() {
        if (this.elements.menuList) this.elements.menuList.removeAll(true);
        if (this.elements.infoPanel) this.elements.infoPanel.removeAll(true);
        if (this.elements.bottomPanel) {
            this.elements.bottomPanel.removeAll(true);
            this.elements.bottomPanel.destroy();
            this.elements.bottomPanel = null;
        }
        if (this.elements.hintText) {
            this.elements.hintText.destroy();
            this.elements.hintText = null;
        }
        this.elements.slotEntries = {};
        this.elements.rightTexts = [];
        this.elements.bottomTexts = [];
    }

    destroy() {
        this.clear();
    }
}
